import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import CustomerTabs from '../components/CustomerHub/CustomerTabs';
import DocumentsTab from '../components/CustomerHub/DocumentsTab';
import EntityMeetings from '../components/meetings/EntityMeetings';
import { WorkLogsExplorer } from '../components/worklogs/GroupedWorkLogs';
import ProjectFormModal from '../components/projects/ProjectFormModal';
import DetailHeader from '../components/details/DetailHeader';
import DetailSection from '../components/details/DetailSection';
import EmptyState from '../components/EmptyState';
import { TicketsTab } from './OrganizationPage';
import { INTERNAL_HUB_PRIMARY_TABS, INTERNAL_HUB_TABS, hubClock, internalHubActiveTab, type InternalHubTab } from '../components/CustomerHub/internalHubModel';
import { formatCurrency } from '../utils/format';

/**
 * D-321 - "עבודה פנימית OTE": the company's own work in one place, built from the customer card's
 * components, with no money (billing, bank, contract, price) and no portal in it. The server decides
 * who sees what; this page only never asks for the forbidden parts.
 */
export default function InternalWorkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const { internalOnly } = usePermissions();
  const [showNewProject, setShowNewProject] = useState(false);
  const activeTab = internalHubActiveTab(searchParams.get('tab'));

  // The last 3 months, like the logs window of a customer card.
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getFullYear(), to.getMonth() - 2, 1);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: iso(from), to: iso(to) };
  }, []);

  const overview = useQuery({ queryKey: ['internal-hub-overview'], queryFn: () => api.getInternalHubOverview() });
  const data = overview.data?.data;
  const orgId = data?.organization.id;

  const setTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'overview') next.delete('tab');
    else next.set('tab', key);
    setSearchParams(next);
  };

  const projects = useQuery({
    queryKey: ['internal-hub-projects', orgId],
    queryFn: () => api.getProjects({ organization_id: orgId }),
    enabled: !!orgId && activeTab === 'projects',
  });

  if (overview.isLoading) return <DetailSection><p className="text-sm text-surface-500">{t('common.loading')}</p></DetailSection>;
  if (overview.isError || !data || !orgId) {
    return (
      <DetailSection>
        <EmptyState icon={Building2} title={t('internalHub.notAvailable')} />
      </DetailSection>
    );
  }

  const tabs = INTERNAL_HUB_TABS.map((key) => ({ key, label: t(`internalHub.tab.${key}`) }));
  const isStaff = user?.role === 'admin' || user?.role === 'agent';

  return (
    <div className="space-y-5">
      <DetailHeader
        breadcrumb={[{ label: t('internalHub.title') }]}
        title={t('internalHub.title')}
        subtitle={t('internalHub.subtitle')}
      />
      <CustomerTabs active={activeTab} onChange={setTab} tabs={tabs} primary={INTERNAL_HUB_PRIMARY_TABS} />

      {activeTab === 'overview' && (
        <DetailSection title={t('internalHub.tab.overview')}>
          <dl className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="internal-hub-overview">
            <Stat label={t('internalHub.openTasks')} value={String(data.open_tasks)} />
            <Stat label={t('internalHub.monthHours')} value={hubClock(data.month_minutes)} />
            <Stat label={t('internalHub.activeProjects')} value={String(data.active_projects)} />
            {data.can_view_cost && data.month_cost != null && (
              <Stat label={t('internalHub.monthCost')} value={formatCurrency(data.month_cost)} />
            )}
          </dl>
        </DetailSection>
      )}

      {activeTab === 'projects' && (
        <DetailSection
          title={t('internalHub.tab.projects')}
          actions={isStaff ? (
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewProject(true)}>
              <Plus size={16} aria-hidden="true" /> {t('internalHub.newProject')}
            </button>
          ) : undefined}
        >
          {projects.isLoading && <p className="text-sm text-surface-500">{t('common.loading')}</p>}
          {projects.isError && <p className="text-sm text-danger-700" role="alert">{t('internalHub.notAvailable')}</p>}
          {projects.data && ((projects.data.data ?? []).length === 0 ? (
            <p className="text-sm text-surface-500">{t('internalHub.noProjects')}</p>
          ) : (
            <ul className="divide-y divide-surface-100">
              {(projects.data.data ?? []).map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <Link to={`/projects/${p.id}`} className="text-primary-700 hover:underline underline-offset-2 min-w-0 truncate">{p.name}</Link>
                  <span className="text-surface-500 flex-shrink-0">
                    {t('internalHub.openTasksOf', { n: p.open_ticket_count ?? 0 })}
                  </span>
                </li>
              ))}
            </ul>
          ))}
        </DetailSection>
      )}

      {activeTab === 'tasks' && <TicketsTab orgId={orgId} mode="task" canCreateTask={isStaff && !internalOnly} />}

      {activeTab === 'hours' && (
        <DetailSection title={t('internalHub.tab.hours')} flush>
          <WorkLogsExplorer queryKey={`internal-${orgId}`} scope={{ organization_id: orgId }} from={range.from} to={range.to} defaultGroupBy="worker" />
        </DetailSection>
      )}

      {activeTab === 'documents' && <DocumentsTab orgId={orgId} canWrite={isStaff && !internalOnly} />}

      {activeTab === 'meetings' && (
        <DetailSection title={t('internalHub.tab.meetings')} flush>
          <EntityMeetings organizationId={orgId} canCreate={isStaff && !internalOnly} bare />
        </DetailSection>
      )}

      {showNewProject && (
        <ProjectFormModal
          mode="create"
          kind="project"
          organizationId={orgId}
          onClose={() => setShowNewProject(false)}
          onSaved={(id) => {
            setShowNewProject(false);
            queryClient.invalidateQueries({ queryKey: ['internal-hub-projects'] });
            queryClient.invalidateQueries({ queryKey: ['internal-hub-overview'] });
            navigate(`/projects/${id}`);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-surface-500">{label}</dt>
      <dd className="mt-0.5 text-xl font-semibold text-surface-900">{value}</dd>
    </div>
  );
}

export type { InternalHubTab };
